<div class="flex flex-col gap-3">
  @for (section of config().sections; track $index) {
    @for (column of section.columns; track $index) {
      @for (field of column.fields; track field.fieldId) {
        @let fieldKind = locked().has(field.fieldId) ? 'locked' : kind(field);
        @let missing = isMissing(field);
        @let id = 'field-' + field.fieldId;
        @let parts = fieldKind === 'text' ? segments(field) : [];
        @let unknown = unknownPlaceholders(parts);
        @let placeholderNote = fieldKind !== 'locked' && hasPlaceholders(value(field));
        @let marked = !!unknown || placeholderNote;
        @let describedBy =
          missing
            ? id + '-error'
            : (unknown ? id + '-unknown ' : '') + (placeholderNote ? id + '-placeholders' : '') ||
              null;

        <div>
          <label
            [for]="id"
            class="block text-xs font-medium tracking-wide text-cmi-gray-mid uppercase">
            {{ label(field.label) }}
            @if (!field.optional) {
              <span aria-hidden="true">*</span>
            }
          </label>

          @switch (fieldKind) {
            @case ('locked') {
              <!-- Each mail fills it in itself: no value to show, nothing to type. -->
              <input
                [id]="id"
                type="text"
                readonly
                [attr.aria-describedby]="id + '-locked'"
                class="mt-1 w-full cursor-not-allowed rounded-md border border-gray-300 bg-cmi-gray-light p-2 text-sm text-cmi-gray" />
              <p [id]="id + '-locked'" class="mt-1 text-xs text-cmi-gray-mid">
                {{ 'documentFields.prefilledPerMail' | translate }}
              </p>
            }
            @case ('text') {
              <div class="mt-1 flex gap-1" [class.items-start]="field.multiline">
                <!-- With braces in it, the text is drawn by the layer behind the input,
                     where each placeholder is marked as known or not. The input keeps
                     the caret and the selection. Marks only colour the text, so it
                     takes the same room in both and stays lined up. -->
                @if (field.multiline) {
                  <div class="relative w-full min-w-0 rounded-md bg-white">
                    <!-- Ebene und Textarea brechen gleich um und halten Platz für die Scrollbar frei, damit die Zeilen übereinanderliegen. -->
                    <div
                      #backdrop
                      aria-hidden="true"
                      [class.hidden]="!marked"
                      class="pointer-events-none absolute inset-0 overflow-hidden rounded-md border border-transparent p-2 text-sm wrap-break-word whitespace-pre-wrap text-cmi-gray [scrollbar-gutter:stable]">
                      <ng-container *ngTemplateOutlet="marks; context: { $implicit: parts }" />
                      <!-- Gibt einem Zeilenumbruch am Ende eine eigene Zeile, wie es die Textarea tut. -->
                      <span>&#8203;</span>
                    </div>
                    <textarea
                      #input
                      [id]="id"
                      rows="3"
                      [value]="shownValue(field)"
                      (input)="onInput(field, $event)"
                      (blur)="onTouch(field)"
                      (scroll)="syncScroll(input, backdrop)"
                      (keyup)="syncScroll(input, backdrop)"
                      (select)="syncScroll(input, backdrop)"
                      [attr.aria-invalid]="missing ? 'true' : null"
                      [attr.aria-describedby]="describedBy"
                      class="relative block w-full resize-y rounded-md border bg-transparent p-2 text-sm [scrollbar-gutter:stable]"
                      [ngClass]="[
                        missing
                          ? 'border-red-500'
                          : unknown
                            ? 'border-amber-500'
                            : 'border-gray-300',
                        marked ? 'text-transparent caret-cmi-gray' : 'text-cmi-gray'
                      ]"></textarea>
                  </div>
                  <ng-container
                    *ngTemplateOutlet="placeholderMenu; context: { $implicit: field, input }" />
                } @else {
                  <div class="relative w-full min-w-0 rounded-md bg-white">
                    <div
                      #backdrop
                      aria-hidden="true"
                      [class.hidden]="!marked"
                      class="pointer-events-none absolute inset-0 overflow-hidden rounded-md border border-transparent p-2 text-sm whitespace-pre text-cmi-gray">
                      <ng-container *ngTemplateOutlet="marks; context: { $implicit: parts }" />
                    </div>
                    <input
                      #input
                      [id]="id"
                      type="text"
                      [value]="shownValue(field)"
                      (input)="onInput(field, $event)"
                      (blur)="onTouch(field)"
                      (scroll)="syncScroll(input, backdrop)"
                      (keyup)="syncScroll(input, backdrop)"
                      (select)="syncScroll(input, backdrop)"
                      [attr.aria-invalid]="missing ? 'true' : null"
                      [attr.aria-describedby]="describedBy"
                      class="relative w-full rounded-md border bg-transparent p-2 text-sm"
                      [ngClass]="[
                        missing
                          ? 'border-red-500'
                          : unknown
                            ? 'border-amber-500'
                            : 'border-gray-300',
                        marked ? 'text-transparent caret-cmi-gray' : 'text-cmi-gray'
                      ]" />
                  </div>
                  <ng-container
                    *ngTemplateOutlet="placeholderMenu; context: { $implicit: field, input }" />
                }
              </div>
            }
            @case ('date') {
              <div class="mt-1 flex gap-1">
                @let dateKey = datePlaceholder(value(field));

                @if (dateKey) {
                  <!-- Stands for a date each mail brings along, so there is no date to pick. -->
                  <input
                    [id]="id"
                    type="text"
                    readonly
                    [value]="'{' + placeholderName(dateKey) + '}'"
                    [attr.aria-describedby]="describedBy"
                    class="w-full min-w-0 rounded-md border border-gray-300 bg-cmi-gray-light p-2 text-sm text-cmi-gray" />
                  <button
                    type="button"
                    (click)="removePlaceholder(field)"
                    [attr.aria-label]="'documentFields.removePlaceholder' | translate"
                    [title]="'documentFields.removePlaceholder' | translate"
                    class="shrink-0 rounded-md border border-gray-300 bg-white px-2 text-sm text-cmi-gray-mid hover:bg-cmi-gray-light">
                    ×
                  </button>
                } @else {
                  <input
                    [id]="id"
                    type="date"
                    [value]="value(field)"
                    (change)="onInput(field, $event)"
                    (blur)="onTouch(field)"
                    [attr.aria-invalid]="missing ? 'true' : null"
                    [attr.aria-describedby]="describedBy"
                    class="w-full min-w-0 rounded-md border bg-white p-2 text-sm text-cmi-gray"
                    [ngClass]="missing ? 'border-red-500' : 'border-gray-300'" />
                }
                <ng-container
                  *ngTemplateOutlet="placeholderMenu; context: { $implicit: field, input: null }" />
              </div>
            }
            @case ('select') {
              @let options = field.options ?? [];

              <div class="relative mt-1">
                <select
                  [id]="id"
                  (change)="onInput(field, $event)"
                  (blur)="onTouch(field)"
                  [disabled]="!options.length"
                  [attr.aria-invalid]="missing ? 'true' : null"
                  [attr.aria-describedby]="
                    !options.length ? id + '-hint' : missing ? id + '-error' : null
                  "
                  class="w-full appearance-none rounded-md border bg-white p-2 pr-8 text-sm text-cmi-gray disabled:bg-cmi-gray-light"
                  [ngClass]="missing ? 'border-red-500' : 'border-gray-300'">
                  <option value="" [selected]="value(field) === ''">
                    {{ 'documentFields.selectPlaceholder' | translate }}
                  </option>
                  @for (option of options; track option.value) {
                    <option [value]="option.value" [selected]="option.value === value(field)">
                      {{ label(option.label) }}
                    </option>
                  }
                </select>
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                  class="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-cmi-gray-mid">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>

              @if (!options.length) {
                <p [id]="id + '-hint'" class="mt-1 text-xs text-cmi-gray-mid">
                  {{ 'documentFields.noOptions' | translate }}
                </p>
              }
            }
          }

          @if (missing) {
            <p [id]="id + '-error'" class="mt-1 text-xs text-red-700">
              {{ 'documentFields.required' | translate }}
            </p>
          } @else {
            @if (unknown) {
              <p [id]="id + '-unknown'" class="mt-1 text-xs text-amber-800">
                {{ 'documentFields.unknownPlaceholder' | translate: { names: unknown } }}
              </p>
            }
            @if (placeholderNote) {
              <p [id]="id + '-placeholders'" class="mt-1 text-xs text-cmi-gray-mid">
                {{ 'documentFields.placeholderHint' | translate }}
              </p>
            }
          }
        </div>
      }
    }
  }
</div>

<!-- Der Text eines Felds, die Platzhalter als bekannt oder unbekannt markiert. -->
<ng-template #marks let-parts>
  @for (segment of parts; track $index) {
    @switch (segment.placeholder) {
      @case ('known') {
        <span class="rounded-sm bg-blue-100 text-blue-800">{{ segment.text }}</span>
      }
      @case ('unknown') {
        <span
          class="rounded-sm bg-amber-100 text-amber-800 underline decoration-amber-600 decoration-wavy"
          >{{ segment.text }}</span
        >
      }
      @default {
        <span>{{ segment.text }}</span>
      }
    }
  }
</ng-template>

<!-- The placeholders that fit the field, put in where the cursor is. -->
<ng-template #placeholderMenu let-field let-input="input">
  @let keys = placeholders(field);

  @if (keys.length) {
    <div data-placeholder-menu (keydown.escape)="onMenuEscape(toggle)" class="relative shrink-0">
      <button
        #toggle
        type="button"
        (click)="toggleMenu(field)"
        aria-haspopup="menu"
        [attr.aria-expanded]="openMenu() === field.fieldId"
        [attr.aria-controls]="'field-' + field.fieldId + '-placeholders-menu'"
        [attr.aria-label]="'documentFields.insertPlaceholder' | translate"
        [title]="'documentFields.insertPlaceholder' | translate"
        class="h-full rounded-md border border-gray-300 bg-white px-2 py-2 font-mono text-sm text-cmi-gray-mid hover:bg-cmi-gray-light">
        {{ '{ }' }}
      </button>

      @if (openMenu() === field.fieldId) {
        <div
          #menu
          [id]="'field-' + field.fieldId + '-placeholders-menu'"
          role="menu"
          class="absolute right-0 z-10 w-48 rounded-md border border-gray-300 bg-white py-1 shadow-md"
          [ngClass]="menuUp() ? 'bottom-full mb-1' : 'top-full mt-1'">
          @for (key of keys; track key) {
            <button
              type="button"
              role="menuitem"
              (click)="onPlaceholder(field, key, input)"
              class="block w-full px-3 py-1.5 text-left text-sm text-cmi-gray hover:bg-cmi-gray-light">
              {{ placeholderName(key) }}
            </button>
          }
        </div>
      }
    </div>
  }
</ng-template>
